/**
 * Sold-out UI sync — Klaviyo BIS + Sold out badge.
 *
 * Client-side variant updates (FBT PDPs) and third-party apps (e.g. Swatch King)
 * can leave stale sold-out UI. This keeps badges and Notify Me in sync.
 */
(function () {
  const IN_STOCK_CLEANUP_MS = [0, 50, 150, 400, 800, 1200];

  function getProductRoot() {
    return document.querySelector('.product.js-product');
  }

  function getVariantPicker() {
    return getProductRoot()?.querySelector('variant-picker') || null;
  }

  function getCurrentVariant() {
    return getVariantPicker()?.variant || null;
  }

  function getBisTrigger() {
    const product = getProductRoot();
    if (!product) return null;
    return product.querySelector('#Klaviyo_button')
      || product.querySelector('.product-info .klaviyo-bis-trigger');
  }

  function isBisExcluded() {
    const productRoot = getProductRoot();
    const tags = (productRoot?.dataset.kcProductTags || '').split('|').filter(Boolean);
    return tags.includes('Not_showing_notify_me');
  }

  function removeInjectedBisButtons() {
    const trigger = getBisTrigger();
    document.querySelectorAll('.product-info .klaviyo-bis-trigger').forEach((el) => {
      if (trigger && el !== trigger) el.remove();
    });
  }

  function setBisHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('data-kc-bis-hidden', 'true');
      el.style.setProperty('display', 'none', 'important');
      el.setAttribute('aria-hidden', 'true');
    } else {
      el.removeAttribute('data-kc-bis-hidden');
      el.style.removeProperty('display');
      el.setAttribute('aria-hidden', 'false');
    }
  }

  function hideAllBisTriggers() {
    const product = getProductRoot();
    if (!product) return;

    product.querySelectorAll('#Klaviyo_button, .klaviyo-bis-trigger').forEach((el) => {
      setBisHidden(el, true);
    });
    removeInjectedBisButtons();
  }

  function shouldShowBis(variant) {
    if (!variant || isBisExcluded()) return false;
    return variant.available === false;
  }

  function syncSoldOutLabels(variant) {
    const product = getProductRoot();
    if (!product) return;

    const showSoldOut = Boolean(variant && variant.available === false);

    product.querySelectorAll('.product-label--sold-out').forEach((label) => {
      label.hidden = !showSoldOut;
      label.style.display = showSoldOut ? '' : 'none';
      label.setAttribute('aria-hidden', showSoldOut ? 'false' : 'true');

      const wrapper = label.closest('.product-label-wrapper');
      if (wrapper) {
        wrapper.hidden = !showSoldOut;
        wrapper.style.display = showSoldOut ? '' : 'none';
      }
    });

    product.querySelectorAll('[data-dynamic-variant-content*="product-labels"]').forEach((block) => {
      if (!showSoldOut) {
        block.hidden = true;
        return;
      }
      const hasVisible = block.querySelector('.product-label--sold-out:not([hidden])');
      block.hidden = !hasVisible;
    });
  }

  function scheduleInStockCleanup(variant) {
    if (!variant || variant.available !== false) {
      const variantId = variant?.id;
      IN_STOCK_CLEANUP_MS.forEach((delay) => {
        setTimeout(() => {
          const current = getCurrentVariant();
          if (!current || (variantId && current.id !== variantId)) return;
          if (current.available === false) return;
          syncSoldOutLabels(current);
          hideAllBisTriggers();
        }, delay);
      });
    }
  }

  function updateBisVisibility(variant) {
    const trigger = getBisTrigger();
    if (!trigger) return;

    if (shouldShowBis(variant)) {
      removeInjectedBisButtons();
      setBisHidden(trigger, false);
      trigger.style.display = 'inline-block';
    } else {
      hideAllBisTriggers();
      scheduleInStockCleanup(variant);
    }

    document.dispatchEvent(new CustomEvent('kc:klaviyo-bis:update'));
  }

  function syncSoldOutUi(variant) {
    const resolved = variant || getCurrentVariant();
    syncSoldOutLabels(resolved);
    updateBisVisibility(resolved);
  }

  function watchThirdPartyReinjection() {
    const product = getProductRoot();
    const info = product?.querySelector('.product-info');
    if (!info || info.dataset.kcSoldOutWatch) return;
    info.dataset.kcSoldOutWatch = '1';

    let pending = null;
    const observer = new MutationObserver(() => {
      clearTimeout(pending);
      pending = setTimeout(() => {
        const variant = getCurrentVariant();
        if (variant && variant.available) {
          syncSoldOutLabels(variant);
          hideAllBisTriggers();
        }
      }, 16);
    });

    observer.observe(info, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'hidden', 'class'],
    });
  }

  function initFromPage() {
    syncSoldOutUi(getCurrentVariant());
    watchThirdPartyReinjection();
  }

  window.__kcSyncSoldOutUi = syncSoldOutUi;
  window.__kcSyncKlaviyoBis = updateBisVisibility;

  document.addEventListener('on:variant:change', (evt) => {
    if (!evt.detail?.variant) return;
    syncSoldOutUi(evt.detail.variant);
  });

  document.addEventListener('change', (evt) => {
    if (evt.target.name !== 'id') return;
    if (!evt.target.closest('.product.js-product')) return;
    setTimeout(() => syncSoldOutUi(getCurrentVariant()), 0);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(initFromPage, 0);
    });
  } else {
    setTimeout(initFromPage, 0);
  }
})();
